import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  useTheme,
  CircularProgress,
  Alert,
  Chip,
  alpha,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  setSearchQuery,
  fetchMunicipalities,
} from "../store/slices/municipalitySlice";

const MunicipalitiesPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    municipalities: rawMunicipalities,
    searchQuery = "",
    loading,
    error,
  } = useSelector((state) => state.municipalities);

  // Медия заявки за различни устройства
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600px - 900px
  const isSmallLaptop = useMediaQuery(theme.breakpoints.between("md", "lg")); // 900px - 1200px (13" лаптопи)
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg")); // > 1200px

  const municipalities = (() => {
    if (Array.isArray(rawMunicipalities)) {
      return rawMunicipalities;
    } else if (
      rawMunicipalities &&
      rawMunicipalities.content &&
      Array.isArray(rawMunicipalities.content)
    ) {
      return rawMunicipalities.content;
    } else if (
      rawMunicipalities &&
      rawMunicipalities.data &&
      Array.isArray(rawMunicipalities.data)
    ) {
      return rawMunicipalities.data;
    }
    return [];
  })();

  const [viewMode, setViewMode] = useState("list");
  const letterRefs = useRef({});

  useEffect(() => {
    console.log("Fetching municipalities...");
    dispatch(fetchMunicipalities());
  }, [dispatch]);

  const handleSearch = (event) => {
    dispatch(setSearchQuery(event.target.value));
  };

  // ФИЛТЪР с правилна структура
  const filteredMunicipalities = Array.isArray(municipalities)
    ? municipalities.filter((municipality) => {
        if (!municipality || !municipality.municipality) return false;
        return municipality.municipality
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
      })
    : [];

  // ФУНКЦИЯ за групиране
  const groupByFirstLetter = (municipalitiesList) => {
    if (!Array.isArray(municipalitiesList) || municipalitiesList.length === 0) {
      return {};
    }

    const groups = {};
    let skipped = 0;

    municipalitiesList.forEach((municipality) => {
      if (!municipality || !municipality.municipality) {
        skipped++;
        return;
      }

      const name = municipality.municipality.trim();
      const firstLetter = name.charAt(0).toUpperCase();

      // Проверка за кирилица
      if (!/[А-Я]/.test(firstLetter)) {
        skipped++;
        return;
      }

      if (!groups[firstLetter]) {
        groups[firstLetter] = [];
      }
      groups[firstLetter].push(municipality);
    });

    return groups;
  };

  const groupedMunicipalities = groupByFirstLetter(filteredMunicipalities);

  // СОРТИРАНЕ на кирилица
  const cyrillicOrder = "АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЬЮЯ";

  const availableLetters = Object.keys(groupedMunicipalities)
    .filter((letter) => cyrillicOrder.includes(letter))
    .sort((a, b) => cyrillicOrder.indexOf(a) - cyrillicOrder.indexOf(b));

  const scrollToLetter = (letter) => {
    if (letterRefs.current[letter]) {
      letterRefs.current[letter].scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Определяне на броя колони според размера на екрана
  const getGridColumns = () => {
    if (isMobile) return 1;
    if (isTablet) return 2;
    if (isSmallLaptop) return 3;
    return 4;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  console.log(municipalities);
  return (
    <Container
      maxWidth="xl"
      disableGutters
      sx={{
        py: {
          xs: 2, // Mobile
          sm: 3, // Tablet
          md: 3, // 13" лаптоп
          lg: 4, // Desktop
        },
        px: {
          xs: 1, // Mobile
          sm: 2, // Tablet
          md: 2, // 13" лаптоп
          lg: 3, // Desktop
          xl: 4, // Голям екран
        },
      }}
    >
      <Box sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontSize: {
              xs: "1.25rem", // Mobile
              sm: "1.5rem", // Tablet
              md: "1.75rem", // 13" лаптоп
              lg: "2rem", // Desktop
              xl: "2.125rem", // Голям екран
            },
            mb: { xs: 1, sm: 1.5, md: 2 },
          }}
        >
          Общински профили
        </Typography>

        <Typography
          variant="subtitle1"
          color="text.secondary"
          paragraph
          sx={{
            fontSize: {
              xs: "0.8rem", // Mobile
              sm: "0.875rem", // Tablet
              md: "0.95rem", // 13" лаптоп
              lg: "1rem", // Desktop
            },
            mb: { xs: 2, sm: 2.5, md: 3 },
          }}
        >
          Азбучен указател на всички {municipalities.length} общини в България
        </Typography>

        <Paper
          elevation={2}
          sx={{
            p: {
              xs: 1.5, // Mobile
              sm: 2, // Tablet
              md: 2.5, // 13" лаптоп
              lg: 3, // Desktop
            },
            mb: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 1.5, sm: 2, md: 2 },
              alignItems: { md: "center" },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Търсете община по име..."
                value={searchQuery}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize={isMobile ? "small" : "medium"} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: {
                      xs: 40, // Mobile
                      sm: 45, // Tablet
                      md: 48, // 13" лаптоп
                      lg: 50, // Desktop
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: {
                      xs: "0.85rem", // Mobile
                      sm: "0.9rem", // Tablet
                      md: "0.95rem", // 13" лаптоп
                      lg: "1rem", // Desktop
                    },
                  },
                }}
              />
            </Box>

            {/* <Box
              sx={{
                minWidth: {
                  xs: "100%", // Mobile
                  md: 180, // Tablet и нагоре
                },
                width: { xs: "100%", md: "auto" },
              }}
            >
              <Tabs
                value={viewMode}
                onChange={(e, newValue) => setViewMode(newValue)}
                variant={isMobile ? "fullWidth" : "standard"}
                centered={!isMobile}
                sx={{
                  "& .MuiTab-root": {
                    fontSize: {
                      xs: "0.75rem", // Mobile
                      sm: "0.8rem", // Tablet
                      md: "0.85rem", // 13" лаптоп
                      lg: "0.9rem", // Desktop
                    },
                    color: theme.palette.text.primary,
                    minHeight: {
                      xs: 40, // Mobile
                      sm: 45, // Tablet
                      md: 48, // 13" лаптоп
                      lg: 50, // Desktop
                    },
                    px: {
                      xs: 1, // Mobile
                      sm: 1.5, // Tablet
                      md: 2, // 13" лаптоп и нагоре
                    },
                  },
                }}
              >
                <Tab label="Азбучен списък" value="list" />
              </Tabs>
            </Box> */}
          </Box>
        </Paper>

        {/* Азбучен индекс */}
        {availableLetters.length > 0 ? (
          <>
            <Paper
              elevation={0}
              sx={{
                p: {
                  xs: 1, // Mobile
                  sm: 1.5, // Tablet
                  md: 2, // 13" лаптоп и нагоре
                },
                mb: { xs: 2.5, sm: 3, md: 4 },
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 0.5,
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
                "&::-webkit-scrollbar": {
                  height: 4,
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#888",
                  borderRadius: 2,
                },
              }}
            >
              {availableLetters.map((letter) => (
                <Chip
                  key={letter}
                  label={letter}
                  onClick={() => scrollToLetter(letter)}
                  sx={{
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: {
                      xs: "0.7rem", // Mobile
                      sm: "0.75rem", // Tablet
                      md: "0.8rem", // 13" лаптоп
                      lg: "0.9rem", // Desktop
                    },
                    height: {
                      xs: 28, // Mobile
                      sm: 32, // Tablet
                      md: 34, // 13" лаптоп
                      lg: 36, // Desktop
                    },
                    "&:hover": {
                      bgcolor: theme.palette.custom.primaryOrange,
                      color: "white",
                    },
                  }}
                />
              ))}
            </Paper>

            <Typography
              variant="subtitle2"
              color="text.secondary"
              paragraph
              sx={{
                fontSize: {
                  xs: "0.7rem", // Mobile
                  sm: "0.75rem", // Tablet
                  md: "0.8rem", // 13" лаптоп
                  lg: "0.875rem", // Desktop
                },
                mb: { xs: 1.5, sm: 2, md: 2.5 },
              }}
            >
              Намерени {filteredMunicipalities.length} общини в{" "}
              {availableLetters.length} азбучни групи
            </Typography>
          </>
        ) : filteredMunicipalities.length > 0 ? (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Има {filteredMunicipalities.length} общини, но не могат да се
            групират по азбучен ред.
          </Alert>
        ) : null}

        {filteredMunicipalities.length === 0 ? (
          <Paper
            sx={{
              p: { xs: 3, sm: 4, md: 5 },
              textAlign: "center",
            }}
          >
            <Typography
              color="text.secondary"
              sx={{ fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" } }}
            >
              Няма намерени общини
            </Typography>
          </Paper>
        ) : viewMode === "list" && availableLetters.length > 0 ? (
          // АЗБУЧЕН СПИСЪК
          <Box>
            {availableLetters.map((letter) => {
              const municipalitiesInGroup = groupedMunicipalities[letter] || [];

              return (
                <Box key={letter} sx={{ mb: { xs: 2.5, sm: 3, md: 4 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 1, sm: 1.5, md: 2 },
                      mb: { xs: 1.5, sm: 2, md: 2 },
                      p: { xs: 1, sm: 1.25, md: 1.5 },
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box
                      sx={{
                        width: {
                          xs: 30, // Mobile
                          sm: 34, // Tablet
                          md: 38, // 13" лаптоп
                          lg: 40, // Desktop
                        },
                        height: {
                          xs: 30, // Mobile
                          sm: 34, // Tablet
                          md: 38, // 13" лаптоп
                          lg: 40, // Desktop
                        },
                        borderRadius: "50%",
                        bgcolor: theme.palette.custom.primaryOrange,
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: {
                          xs: "1rem", // Mobile
                          sm: "1.125rem", // Tablet
                          md: "1.25rem", // 13" лаптоп
                          lg: "1.5rem", // Desktop
                        },
                        fontWeight: "bold",
                        flexShrink: 0,
                      }}
                    >
                      {letter}
                    </Box>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        fontSize: {
                          xs: "1rem", // Mobile
                          sm: "1.125rem", // Tablet
                          md: "1.25rem", // 13" лаптоп
                          lg: "1.5rem", // Desktop
                        },
                        lineHeight: 1.2,
                      }}
                      ref={(el) => {
                        if (el) letterRefs.current[letter] = el;
                      }}
                    >
                      Буква "{letter}" ({municipalitiesInGroup.length} общини)
                    </Typography>
                  </Box>

                  <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
                    {municipalitiesInGroup.map((municipality) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        xl={2}
                        key={municipality.id}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            p: {
                              xs: 1.25, // Mobile
                              sm: 1.5, // Tablet
                              md: 1.75, // 13" лаптоп
                              lg: 2, // Desktop
                            },
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            "&:hover": {
                              borderColor: "primary.main",
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                              transform: "translateY(-2px)",
                              boxShadow: 1,
                            },
                            transition: "all 0.2s",
                            cursor: "pointer",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                          onClick={() => {
                            if (municipality.municipalityCode) {
                              navigate(
                                `/municipalities/${municipality.municipalityCode}`
                              );
                            }
                          }}
                        >
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 600,
                                mb: 0.5,
                                fontSize: {
                                  xs: "0.85rem", // Mobile
                                  sm: "0.9rem", // Tablet
                                  md: "0.95rem", // 13" лаптоп
                                  lg: "1rem", // Desktop
                                },
                                lineHeight: 1.3,
                                minHeight: {
                                  xs: 40, // Mobile
                                  sm: 44, // Tablet
                                  md: 48, // 13" лаптоп и нагоре
                                },
                              }}
                            >
                              {municipality.municipality}
                            </Typography>

                            {municipality.district && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  mb: 1,
                                  fontSize: {
                                    xs: "0.75rem", // Mobile
                                    sm: "0.8rem", // Tablet
                                    md: "0.85rem", // 13" лаптоп и нагоре
                                  },
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <span style={{ fontSize: "0.9em" }}>📍</span>{" "}
                                {municipality.district}
                              </Typography>
                            )}

                            {municipality.municipalityPopulation && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  fontSize: {
                                    xs: "0.75rem", // Mobile
                                    sm: "0.8rem", // Tablet
                                    md: "0.85rem", // 13" лаптоп и нагоре
                                  },
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  mb: 0.5,
                                }}
                              >
                                <span style={{ fontSize: "0.9em" }}>👥</span>{" "}
                                {new Intl.NumberFormat("bg-BG").format(
                                  municipality.municipalityPopulation
                                )}{" "}
                                жители
                              </Typography>
                            )}
                          </Box>

                          {municipality.totalChitalishta > 0 && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: "success.main",
                                mt: 1,
                                fontSize: {
                                  xs: "0.75rem", // Mobile
                                  sm: "0.8rem", // Tablet
                                  md: "0.85rem", // 13" лаптоп и нагоре
                                },
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <span style={{ fontSize: "0.9em" }}>📚</span>{" "}
                              {municipality.totalChitalishta} читалища
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            })}
          </Box>
        ) : (
          // Ако няма букви но има данни
          <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
            {filteredMunicipalities.map((municipality) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={municipality.id}>
                <Paper
                  elevation={2}
                  sx={{
                    p: { xs: 2, sm: 2.5, md: 3 },
                    borderRadius: 2,
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                    },
                    height: "100%",
                  }}
                  onClick={() => {
                    if (municipality.municipalityCode) {
                      navigate(
                        `/municipalities/${municipality.municipalityCode}`
                      );
                    }
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{
                      fontSize: {
                        xs: "0.9rem", // Mobile
                        sm: "1rem", // Tablet
                        md: "1.1rem", // 13" лаптоп
                        lg: "1.125rem", // Desktop
                      },
                      mb: 1,
                    }}
                  >
                    {municipality.municipality}
                  </Typography>
                  {municipality.district && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: {
                          xs: "0.8rem", // Mobile
                          sm: "0.85rem", // Tablet
                          md: "0.9rem", // 13" лаптоп
                          lg: "0.95rem", // Desktop
                        },
                      }}
                    >
                      Област: {municipality.district}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Container>
  );
};

export default MunicipalitiesPage;
